import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import store, { useAppSelector } from '../../../store';
import { PhotoImage } from '../../../components/common/PhotoImage';
import { Button } from '../../../components/ui/button';
import Toast from '../../../components/Toast/Toast';
import { Admin_UpdatePost, Admin_GetPostAttachmentSasUrl } from '../../../constants/adminData';
import { getSensitiveData } from '@/utils/secureStorage';
import { UserProfileData, PostItem, Attachment } from '@/types/admin';
import DashboardLayout from '../../../components/DashboardLayout';
import {
  PostEditor,
  PostEditorHandle,
  AttachmentUploader,
  getPostWordCount,
  VisibilitySelector,
  VisibilityOption,
} from './PostComponents';
import styles from './createPost.module.css';

const MAX_POST_WORD_COUNT = 2000;
const MAX_POST_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_POST_FILE_SIZE_LABEL = '5 MB';
const MAX_POST_IMAGE_COUNT = 3;
const MAX_TOTAL_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

interface EditPostProps {
  post: PostItem;
  onBack: () => void;
  onUpdate?: (updatedPost: PostItem) => void;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cleanLinkTextContent(raw: string): string {
  const mdMatch = raw.match(/^\[.*?\]\((https?:\/\/.+?)\)$/);
  if (mdMatch) return mdMatch[1];
  return raw;
}

function extractAttachmentUrl(value: unknown): string | null {
  if (typeof value === 'string') {
    const markdownLink = value.match(/^\[.*?\]\((https?:\/\/.+?)\)$/);
    return markdownLink?.[1] || value;
  }
  if (value && typeof value === 'object') {
    const fileUrl = (value as { fileUrl?: unknown }).fileUrl;
    return extractAttachmentUrl(fileUrl);
  }
  return null;
}

function buildEditorHtmlFromContentBlocks(
  contentBlocks: any[],
  attachments: Attachment[]
): string {
  const sorted = [...contentBlocks].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  return sorted
    .map((block) => {
      if (block.blockType?.toUpperCase() === 'IMAGE') {
        const attachment = attachments.find(
          (att) => String(att.attachmentId) === String(block.attachmentId)
        );
        const src = attachment?.fileUrl;
        if (!src) return '';
        return `<div><img src="${escapeHtml(src)}" data-attachment-id="${escapeHtml(
          String(block.attachmentId)
        )}" /></div>`;
      }

      if (block.blockType?.toUpperCase() === 'LINK') {
        const href = cleanLinkTextContent(block.textContent || '');
        return `<div><a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(href)}</a></div>`;
      }

      if (
        block.blockType?.toUpperCase() === 'DOCUMENT' ||
        block.blockType?.toUpperCase() === 'FILE'
      ) {
        return '';
      }

      if (block.blockType?.toUpperCase() === 'TEXT') {
        return `<div>${escapeHtml(block.textContent || '')}</div>`;
      }

      return '';
    })
    .join('');
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function MobileHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className={styles.mobilePostHeader}>
      <button
        className={styles.mobileIconButton}
        type="button"
        onClick={onBack}
        aria-label="Go back"
      >
        <ArrowLeft className={styles.mobileHeaderIcon} />
      </button>
      <h1 className={styles.mobilePostTitle}>Edit Post</h1>
      <div />
    </div>
  );
}
function TipsPanel() {
  return (
    <aside className={styles.tipsPanel}>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Tips for a great post</h3>
        <div className={styles.tipItem}>
          <strong>Be authentic</strong>
          <p>Share your thoughts and experiences.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Add media</strong>
          <p>Images and videos get more engagement.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Ask questions</strong>
          <p>Encourage others to engage.</p>
        </div>
        <div className={styles.tipItem}>
          <strong>Keep it relevant</strong>
          <p>Add value to your network.</p>
        </div>
      </div>
    </aside>
  );
}

export default function EditPost({ post, onBack, onUpdate }: EditPostProps) {
  const profile = useAppSelector((state) => state.userProfile);
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    () => (post.attachments || []).map((attachment) => ({
      ...attachment,
      fileUrl: extractAttachmentUrl(attachment.fileUrl),
    }))
  );
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);

  const initialEditorHtml = useMemo(
    () =>
      post.contentBlocks?.length
        ? buildEditorHtmlFromContentBlocks(post.contentBlocks, existingAttachments)
        : post.content || '',
    [post, existingAttachments]
  );

  const [postText, setPostText] = useState(initialEditorHtml);
  const [visibility, setVisibility] = useState<VisibilityOption>(
    post.visibilityId === 1 ? 'Public' : post.visibilityId === 2 ? 'Your Network' : 'Only Me'
  );

  // All new files (images + non-images) added during this edit session.
  // Index in this array = data-new-file-index on <img> DOM node = index in FormData files[].
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const selectedFilesRef = useRef<File[]>([]);

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef<PostEditorHandle | null>(null);

  const userName =
    userData?.name ||
    `${profile.personalInfo.firstName || ''} ${profile.personalInfo.lastName || ''}`.trim() ||
    'Guest User';

  useEffect(() => {
    const loadUrls = async () => {
      const updated = await Promise.all(
        (post.attachments || []).map(async (att) => {
          const isImage = (att.fileType || att.contentType || '').toLowerCase().includes('image');
          if (isImage && att.attachmentId) {
            try {
              const response = await Admin_GetPostAttachmentSasUrl(Number(att.attachmentId));
              const fileUrl =
                extractAttachmentUrl(response.data?.data) ??
                extractAttachmentUrl(response.data?.fileUrl);
              return {
                ...att,
                fileUrl: fileUrl ?? null,
              };
            } catch {
              return att;
            }
          }
          return att;
        })
      );
      setExistingAttachments(updated);
    };
    loadUrls();
  }, [post]);

  useEffect(() => {
    const loadProfile = async () => {
      const stored = await getSensitiveData('userData');
      if (!stored) return;
      setUserData(JSON.parse(stored));
    };
    loadProfile();
  }, []);

  useEffect(() => {
    setPostText(initialEditorHtml);
  }, [initialEditorHtml]);

  const showToastMessage = (message: string, type: 'success' | 'error' | 'warning') => {
    setToast({ message, type });
  };

  const handleDownloadExistingAttachment = async (attachment: Attachment) => {
    try {
      const response = await Admin_GetPostAttachmentSasUrl(attachment.attachmentId);
      const url = extractAttachmentUrl(response.data?.data) ?? extractAttachmentUrl(response.data?.fileUrl);
      if (!url) throw new Error('Download URL is unavailable.');

      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName || 'attachment';
      link.click();
    } catch (error) {
      console.error('Error downloading post attachment:', error);
      showToastMessage('Unable to download the attachment. Please try again.', 'error');
    }
  };

  // Non-image files: only tracked in state + shown in file list. NOT inserted into editor.
  const handleFilesChange = (files: File[]) => {
    if (!files.length) return;
    const validFiles = files.filter((file) => file.size <= MAX_POST_FILE_SIZE_BYTES);

    if (validFiles.length !== files.length) {
      showToastMessage(
        `Each attachment must be ${MAX_POST_FILE_SIZE_LABEL} or smaller.`,
        'error'
      );
    }

    if (!validFiles.length) return;

    const updated = [...selectedFilesRef.current, ...validFiles];
    selectedFilesRef.current = updated;
    setSelectedFiles(updated);
  };

  const handleRemoveFile = (index: number) => {
    const updated = selectedFilesRef.current.filter((_, fileIndex) => fileIndex !== index);
    selectedFilesRef.current = updated;
    setSelectedFiles(updated);
  };

  // Images: inserted into editor at cursor. fileIndex = position in selectedFiles array.
  const handleAddImage = (file: File) => {
    if (file.size > MAX_POST_FILE_SIZE_BYTES) {
      showToastMessage(`Images must be ${MAX_POST_FILE_SIZE_LABEL} or smaller.`, 'error');
      return;
    }

    const existingImageCount = existingAttachments.filter((attachment) =>
      (attachment.fileType || attachment.contentType || '').toLowerCase().startsWith('image') ||
      attachment.fileType?.toLowerCase() === 'image'
    ).length;
    const newImageCount = selectedFilesRef.current.filter((item) =>
      item.type.startsWith('image/')
    ).length;

    if (existingImageCount + newImageCount >= MAX_POST_IMAGE_COUNT) {
      showToastMessage(`You can add up to ${MAX_POST_IMAGE_COUNT} images to a post.`, 'warning');
      return;
    }

    const fileIndex = selectedFilesRef.current.length;
    editorRef.current?.insertImage(file, fileIndex);
    const updated = [...selectedFilesRef.current, file];
    selectedFilesRef.current = updated;
    setSelectedFiles(updated);
  };

  const handleAddLink = () => {
    if (!linkValue.trim()) return;
    editorRef.current?.insertLink(linkValue.trim());
    setLinkValue('');
    setShowLinkModal(false);
  };

  const hasChanges = () => {
    const textChanged = postText !== initialEditorHtml;
    const visibilityChanged =
      (visibility === 'Public' && post.visibilityId !== 1) ||
      (visibility === 'Your Network' && post.visibilityId !== 2) ||
      (visibility === 'Only Me' && post.visibilityId !== 3);
    const attachmentsChanged =
      selectedFilesRef.current.length > 0 || removedAttachmentIds.length > 0;
    return textChanged || visibilityChanged || attachmentsChanged;
  };

  const buildUpdatePayload = async () => {
    const editor = editorRef.current?.getEditorRoot();
    if (!editor) return null;

    let displayOrder = 1;

    // ─── STEP 1: Walk editor DOM → build IMAGE/TEXT/LINK content blocks ──────
    // Also collect which selectedFiles indexes are still in the editor (for images).
    const editorBlocks: any[] = [];
    const referencedImageIndexes = new Set<number>(); // selectedFiles index

    const visitNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        editorBlocks.push({
          blockId: 0,
          blockType: 'TEXT',
          textContent: node.textContent.trim(),
          attachmentId: null,
          metaData: null,
          displayOrder: displayOrder++,
        });
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toUpperCase();

        if (element.dataset.editorImageRemove !== undefined) return;

        if (tagName === 'IMG') {
          const attachmentId = Number(element.dataset.attachmentId);
          const fileIndex = Number(element.dataset.newFileIndex);
          const isExisting = !Number.isNaN(attachmentId) && attachmentId > 0;
          const isNewImage = !isExisting && !Number.isNaN(fileIndex) && fileIndex >= 0;

          if (isNewImage) referencedImageIndexes.add(fileIndex);

          editorBlocks.push({
            blockId: 0,
            blockType: 'IMAGE',
            textContent: null,
            attachmentId: isExisting ? attachmentId : null,
            metaData: null,
            displayOrder: displayOrder++,
            // Temporary field — stripped before sending. Used to patch newFileIndex below.
            _rawFileIndex: isNewImage ? fileIndex : null,
          });
          return;
        }

        if (tagName === 'A') {
          const href = cleanLinkTextContent(
            element.getAttribute('href') || element.textContent || ''
          );
          editorBlocks.push({
            blockId: 0,
            blockType: 'LINK',
            textContent: href,
            attachmentId: null,
            metaData: null,
            displayOrder: displayOrder++,
          });
          return;
        }

        element.childNodes.forEach(visitNode);
      }
    };

    Array.from(editor.childNodes).forEach(visitNode);

    // ─── STEP 2: Build the ordered files list for FormData ───────────────────
    // Rule: images first (only those still in editor), then non-image files.
    // The position of each file in this array = the newFileIndex the backend uses.
    const currentFiles = selectedFilesRef.current;
    const referencedNewFiles: File[] = [];

    // Map: selectedFiles index → formData position (only for new images)
    const imageFormDataIndexMap = new Map<number, number>();
    let formDataPos = 0;

    // Images first — only referenced ones
    currentFiles.forEach((file, selectedIndex) => {
      if (file.type.startsWith('image/') && referencedImageIndexes.has(selectedIndex)) {
        imageFormDataIndexMap.set(selectedIndex, formDataPos++);
        referencedNewFiles.push(file);
      }
    });

    // Non-image files after — always included (they live only in the file list)
    // Track their formData position too so we can build FILE content blocks
    const fileFormDataIndexMap = new Map<number, number>(); // selectedFiles index → formData pos
    currentFiles.forEach((file, selectedIndex) => {
      if (!file.type.startsWith('image/')) {
        fileFormDataIndexMap.set(selectedIndex, formDataPos++);
        referencedNewFiles.push(file);
      }
    });

    // ─── STEP 3: Patch IMAGE blocks — replace _rawFileIndex with newFileIndex ─
    const patchedEditorBlocks = editorBlocks.map((block) => {
      const { _rawFileIndex, ...rest } = block;
      if (block.blockType === 'IMAGE' && _rawFileIndex !== null && _rawFileIndex !== undefined) {
        return {
          ...rest,
          // newFileIndex tells backend: "upload files[newFileIndex] for this IMAGE block"
          newFileIndex: imageFormDataIndexMap.get(_rawFileIndex) ?? null,
        };
      }
      return rest;
    });

    // ─── STEP 4: Build FILE content blocks for new non-image files ────────────
    // Mirrors exactly how CreatePost does it.
    // These come AFTER editor blocks in displayOrder.
    const fileBlocks: any[] = [];
    currentFiles.forEach((file, selectedIndex) => {
      if (!file.type.startsWith('image/')) {
        const formIdx = fileFormDataIndexMap.get(selectedIndex)!;
        fileBlocks.push({
          blockId: 0,
          blockType: 'FILE',
          textContent: null,
          attachmentId: null,
          // newFileIndex tells backend: "upload files[newFileIndex] for this FILE block"
          newFileIndex: formIdx,
          metaData: JSON.stringify({ fileName: file.name }),
          displayOrder: displayOrder++,
        });
      }
    });

    const finalContentBlocks = [...patchedEditorBlocks, ...fileBlocks];

    if (finalContentBlocks.length === 0) return null;

    // ─── STEP 5: Build attachments metadata ──────────────────────────────────
    // Existing attachments (minus removed ones)
    const existingMetadata = existingAttachments
      .filter((att) => !removedAttachmentIds.includes(Number(att.attachmentId)))
      .map((att) => ({
        attachmentId: att.attachmentId,
        fileUrl: att.fileUrl,
        fileName: att.fileName,
        fileType: att.fileType,
        contentType: att.contentType,
        fileSize: att.fileSize,
        displayOrder: att.displayOrder,
      }));

    // New file metadata — order matches referencedNewFiles (FormData order)
    let newDisplayOrder = displayOrder;
    const newFileMetadata = referencedNewFiles.map((file) => ({
      attachmentId: 0,
      fileUrl: null,
      fileName: file.name,
      fileType: file.type.startsWith('image/') ? 'IMAGE' : 'FILE',
      contentType: file.type,
      fileSize: file.size,
      displayOrder: newDisplayOrder++,
    }));

    const content = finalContentBlocks
      .filter((b) => b.blockType === 'TEXT')
      .map((b) => b.textContent)
      .join(' ');

    return {
      postId: post.postId,
      userId: store.getState().userProfile.userId || (await getSensitiveData('userId')),
      content,
      postTypeId: post.postTypeId,
      visibilityId: visibility === 'Public' ? 1 : visibility === 'Your Network' ? 2 : 3,
      attachments: [...existingMetadata, ...newFileMetadata],
      removedAttachmentIds,
      contentBlocks: finalContentBlocks,
      referencedNewFiles, // used only for FormData — not JSON serialized
    };
  };

  const handleUpdate = async () => {
    if (!hasChanges()) {
      showToastMessage('No changes to update', 'warning');
      return;
    }

    if (getPostWordCount(postText) > MAX_POST_WORD_COUNT) {
      showToastMessage(`Post content cannot exceed ${MAX_POST_WORD_COUNT}.`, 'warning');
      return;
    }

    if (selectedFilesRef.current.some((file) => file.size > MAX_POST_FILE_SIZE_BYTES)) {
      showToastMessage(`Each attachment must be ${MAX_POST_FILE_SIZE_LABEL} or smaller.`, 'error');
      return;
    }

    const totalUploadSize = selectedFilesRef.current.reduce(
      (total, file) => total + file.size,
      0
    );
    if (totalUploadSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
      showToastMessage('Total attachment size cannot exceed 10 MB.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = await buildUpdatePayload();
      if (!payload) {
        showToastMessage('Post cannot be empty', 'warning');
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append('postId', String(payload.postId));
      formData.append('userId', payload.userId!);
      formData.append('content', payload.content);
      formData.append('postTypeId', String(payload.postTypeId));
      formData.append('visibilityId', String(payload.visibilityId));
      formData.append('contentBlocks', JSON.stringify(payload.contentBlocks));
      formData.append('attachments', JSON.stringify(payload.attachments));
      formData.append('removedAttachmentIds', JSON.stringify(payload.removedAttachmentIds));

      // Files in same order as newFileMetadata so backend index matches
      payload.referencedNewFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await Admin_UpdatePost(formData);
      if (!response?.success) {
        showToastMessage(response?.message || 'Failed to update post', 'error');
        return;
      }

      showToastMessage('Post updated successfully!', 'success');

      // The API response now contains the saved attachment. Remove its temporary
      // local preview before applying that response so the file is not shown twice.
      selectedFilesRef.current = [];
      setSelectedFiles([]);

      if (onUpdate) {
        onUpdate({ ...response.data });
      }

      setTimeout(() => {
        onBack();
      }, 1500);
    }  catch (error: any) {
  showToastMessage('Failed to update post', 'error');
}finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Edit Post">
      <div className={styles.pageWrapper}>
        <div className={styles.columns}>
          <main className={styles.mainColumn}>
            <div className={styles.card}>
              <MobileHeader onBack={onBack} />
              <div className={styles.postTopRow}>
                <button className={styles.backButton} type="button" onClick={onBack}>
                  ← Cancel
                </button>
                <div />
              </div>

              <div className={styles.profileBlock}>
                <div className={styles.profileAvatarLarge}>
                  {userData?.photoId ? (
                    <PhotoImage
                      photoId={userData.photoId}
                      alt={userName}
                      className={styles.profileAvatarImage}
                    />
                  ) : (
                    <span>{getInitials(userName)}</span>
                  )}
                </div>
                <div>
                  <p className={styles.profileName}>{userName}</p>
                  <p className={styles.profileRole}>
                    {userData?.itSpecialtyName || 'N/A'} 
                  </p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="editPostContent">
                  What's on your mind?
                </label>
                <PostEditor
                  ref={editorRef}
                  value={postText}
                  onChange={setPostText}
                  placeholder="Share an update, ask a question, or post something meaningful."
                  maxWords={MAX_POST_WORD_COUNT}
                  onMaxWordsReached={() =>
                    showToastMessage(`Post content cannot exceed ${MAX_POST_WORD_COUNT}.`, 'warning')
                  }
                />
                <div className={styles.charCount}>
                  {getPostWordCount(postText)}/{MAX_POST_WORD_COUNT}
                </div>
              </div>

              <AttachmentUploader
                files={selectedFiles}
                existingAttachments={existingAttachments}
                onFilesChange={handleFilesChange}
                onRemoveFile={handleRemoveFile}
                onAddImage={handleAddImage}
                onAddLink={() => setShowLinkModal(true)}
                onRemoveExistingAttachment={(attachmentId) => {
                  setExistingAttachments((prev) =>
                    prev.filter((x) => x.attachmentId !== attachmentId)
                  );
                  setRemovedAttachmentIds((prev) => [...prev, attachmentId]);
                }}
                onDownloadExistingAttachment={handleDownloadExistingAttachment}
              />

              <VisibilitySelector visibility={visibility} onChange={setVisibility} />

              <div className={styles.updateActions}>
                <button
                  className={styles.cancelButton}
                  type="button"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <Button
                  className={styles.updateButton}
                  onClick={handleUpdate}
                  disabled={!hasChanges() || isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Post'}
                </Button>
              </div>
            </div>
          </main>
          <TipsPanel />
        </div>
      </div>

      {showLinkModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle}>Add Link</h2>
            <input
              type="url"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="https://example.com"
              className={styles.modalInput}
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalSecondary}
                onClick={() => setShowLinkModal(false)}
              >
                Cancel
              </button>
              <button type="button" className={styles.modalPrimary} onClick={handleAddLink}>
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </DashboardLayout>
  );
}
